import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import Flex from "../../../../../components/Flex";
import { PdfGenerator } from "../../../../../components/PdfGenerator";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { downloadPdf } from "../../../../../hooks/use-download-pdf";
import { EraControllerApi } from "../../../../../services";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  getConfigObject,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { sumEraCptAmounts } from "./era-cpt";

interface LineItem {
  dateOfService: string;
  procedure: string;
  units: number;
  modifiers: string;
  billedAmount: number;
  coveredAllowedAmount: number;
  paidAmount: number;
  patientResponsibility: number;
  patientResponsibilityCode?: string;
  providerAdjustment: number;
  providerAdjustmentCode?: string;
  payerInitiated: string;
  payerProcessedProcedure: string;
  remark: string;
}

interface EraEobData {
  uuid: string;
  checkEftDate: string;
  checkEftNumber: string;
  patientName: string;
  encounterDate: string;
  invoiceNumber: string;
  payerName: string;
  payerId: string;
  payerAddress: string;
  payerContactName: string;
  payerEmail: string;
  payeeName: string;
  payeeAddress: string;
  payeeNpi: string;
  checkEftAmount: number;
  eraGeneratedDate: string;
  paymentMethod: string;
  claimCharge: number;
  claimPayment: number;
  payerClaimControlNumber: string;
  patientControlNumber: string;
  memberId: string;
  status: string;
  insuredName: string;
  lineItems: LineItem[];
}

interface ViewEraEobProps {
  disclosureHost: DisclosureHost;
  eraEobUuid?: string | { uuid?: string; autoDownload?: boolean };
  onAutoDownloadComplete?: () => void;
  title?: string;
}

const ViewEraEob = ({
  disclosureHost,
  eraEobUuid,
  onAutoDownloadComplete,
  title = "EOB Details",
}: ViewEraEobProps) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfRef = useRef<{ generatePdf: () => void }>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const autoDownloadTriggeredRef = useRef(false);

  const eraUuid =
    typeof eraEobUuid === "string" ? eraEobUuid : (eraEobUuid?.uuid ?? "");
  const isAutoDownload =
    typeof eraEobUuid === "object" ? !!eraEobUuid?.autoDownload : false;
  const shouldLoadEraData =
    !!eraUuid && (disclosureHost.opened || isAutoDownload);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["get-era-eob-detail", eraUuid],
    enabled: shouldLoadEraData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const api = new EraControllerApi(getConfigObject());
      const [eraRes] = await Promise.all([
        api.getEraByUuid(eraUuid, getXtenantId()),
        // api.getEraClaimsByEraUuid(eraUuid, getXtenantId()),
      ]);

      const era = (eraRes?.data as any)?.data ?? {};
      // const claimsRaw = (claimsRes?.data as any)?.data;
      // const claims = Array.isArray(claimsRaw)
      //   ? claimsRaw
      //   : Array.isArray(claimsRaw?.content)
      //     ? claimsRaw.content
      //     : [];

      return { era };
    },
  });

  useEffect(() => {
    if (!data) return;
  }, [data]);

  const eraEobData = useMemo<EraEobData>(() => {
    const era = (data as any)?.era ?? {};
    const claims = ((data as any)?.era?.claims ?? []) as any[];
    const firstClaim = claims[0] ?? {};

    const getFirst = (...vals: any[]) =>
      vals.find((val) => val !== undefined && val !== null && val !== "") ??
      "-";
    const getAddr = (addr: any) => {
      if (!addr) return "-";
      if (typeof addr === "string") return addr;
      return [
        addr?.line1,
        addr?.line2,
        addr?.city,
        addr?.state,
        addr?.zipcode,
        addr?.country,
      ]
        .filter(Boolean)
        .join(", ");
    };
    const getObjVal = (obj: any) => getValueFromObj(obj) ?? "-";
    const eraPayer = era?.insurancePayer ?? firstClaim?.insurancePayer ?? {};
    const eraBillingProvider =
      era?.billingProvider ?? firstClaim?.billingProvider ?? {};

    const lineItems: LineItem[] = claims.flatMap((claim) => {
      const patientProcedureCodes = (claim?.patientProcedureCodes ??
        []) as any[];

      // Allowed and Paid are the claim's totals, summed from its procedure
      // lines with the same formula the claim level uses. The payer's stored
      // claim.allowedAmount reports the paid amount, so it is not read here.
      const claimTotals = sumEraCptAmounts(
        patientProcedureCodes,
        claim?.groupCode
      );

      if (!patientProcedureCodes.length) {
        return [
          {
            dateOfService: formatDate(claim?.adjudicationDate) ?? "-",
            procedure: "-",
            units: 0,
            modifiers: "-",
            billedAmount: Number(claim?.chargeAmount ?? 0),
            coveredAllowedAmount: Number(claim?.allowedAmount ?? 0),
            paidAmount: Number(claim?.insurancePaid ?? 0),
            patientResponsibility: Number(claim?.patientResponsibility ?? 0),
            patientResponsibilityCode: undefined,
            providerAdjustment: Number(claim?.insuranceAdjustment ?? 0),
            providerAdjustmentCode: [claim?.groupCode, claim?.reasonCode]
              .filter(Boolean)
              .join(" "),
            payerInitiated: claim?.denialRejectionCode ?? "",
            payerProcessedProcedure: "-",
            remark: claim?.statusCode ?? "",
          },
        ];
      }

      return patientProcedureCodes.map((code) => ({
        dateOfService:
          formatDate(claim?.adjudicationDate) ??
          formatDate(claim?.paymentDate) ??
          "-",
        procedure: getValueFromObj(code?.procedureCode) ?? "-",
        units: Number(code?.quantity ?? 0),
        modifiers:
          getValueFromObj(code?.modifiers) ?? code?.medicalCodeModifiers ?? "-",
        billedAmount: Number(code?.charges ?? code?.total ?? 0),
        coveredAllowedAmount: claimTotals.allowed ?? 0,
        paidAmount: claimTotals.insurancePaid ?? 0,
        patientResponsibility: Number(claim?.patientResponsibility ?? 0),
        patientResponsibilityCode: undefined,
        providerAdjustment: Number(
          claim?.insuranceAdjustment ?? code?.writeOff ?? 0
        ),
        providerAdjustmentCode: [claim?.groupCode, claim?.reasonCode]
          .filter(Boolean)
          .join(" "),
        payerInitiated: claim?.denialRejectionCode ?? "",
        payerProcessedProcedure: getValueFromObj(code?.procedureCode) ?? "-",
        remark: claim?.statusCode ?? "",
      }));
    });

    return {
      uuid: era?.uuid ?? eraUuid,
      checkEftDate: formatDate(era?.eraDate) ?? "-",
      checkEftNumber:
        era?.checkEftNumber ??
        era?.instrumentNumber ??
        era?.eraDocNumber ??
        "-",
      patientName: getFirst(
        firstClaim?.patientName,
        firstClaim?.insuredName,
        getObjVal(firstClaim?.patient),
        "Unknown Patient"
      ),
      encounterDate:
        formatDate(firstClaim?.adjudicationDate) ??
        formatDate(firstClaim?.paymentDate) ??
        formatDate(era?.eraDate) ??
        "-",
      invoiceNumber:
        firstClaim?.claimId?.split("-")?.[0]?.toUpperCase() ??
        firstClaim?.claimId ??
        "-",
      eraDocNumber: era?.eraDocNumber ?? "-",
      payerName: data?.era?.payer ? getValueFromObj(data?.era?.payer) : "-",
      payerId: data?.era?.payer ? getIdFromObj(data?.era?.payer) : "-",
      payerAddress: getFirst(
        getAddr(eraPayer?.address),
        getAddr(era?.payerAddress),
        getAddr(firstClaim?.payerAddress)
      ),
      payerContactName: getFirst(
        eraPayer?.contactName,
        era?.payerContactName,
        firstClaim?.payerContactName
      ),
      payerEmail: getFirst(
        eraPayer?.email,
        era?.payerEmail,
        firstClaim?.payerEmail
      ),
      payeeName: getFirst(
        era?.billingProviderName,
        getObjVal(eraBillingProvider),
        getObjVal(firstClaim?.provider),
        getObjVal(firstClaim?.billingProvider)
      ),
      payeeAddress: getFirst(
        getAddr(era?.billingProviderAddress),
        getAddr((eraBillingProvider as any)?.address),
        getAddr(firstClaim?.billingProviderAddress),
        getAddr(firstClaim?.providerAddress)
      ),
      payeeNpi: getFirst(
        era?.billingProviderNpi,
        (eraBillingProvider as any)?.npi,
        firstClaim?.billingProviderNpi,
        firstClaim?.providerNpi
      ),
      checkEftAmount: Number(era?.amount ?? 0),
      eraGeneratedDate: formatDate(era?.eraDate) ?? "-",
      paymentMethod: replaceUnderScoreAddSpace(era?.paymentMethod ?? "-"),
      claimCharge: claims.reduce(
        (acc, claim) => acc + Number(claim?.chargeAmount ?? 0),
        0
      ),
      claimPayment: claims.reduce(
        (acc, claim) => acc + Number(claim?.insurancePaid ?? 0),
        0
      ),
      payerClaimControlNumber: firstClaim?.payersClaimId ?? "-",
      patientControlNumber:
        firstClaim?.claimId?.split("-")?.[0]?.toUpperCase() ??
        firstClaim?.claimId ??
        "-",
      memberId: getFirst(firstClaim?.memberId, firstClaim?.insuranceId),
      status: replaceUnderScoreAddSpace(firstClaim?.claimStatus ?? "-"),
      insuredName: getFirst(
        firstClaim?.insuredName,
        firstClaim?.patientName,
        getObjVal(firstClaim?.patient)
      ),
      lineItems,
    } as any;
  }, [data, eraUuid]);

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const calculateTotals = () => {
    const totals = eraEobData.lineItems.reduce(
      (acc, item) => ({
        billedAmount: acc.billedAmount + item.billedAmount,
        coveredAllowedAmount:
          acc.coveredAllowedAmount + item.coveredAllowedAmount,
        paidAmount: acc.paidAmount + item.paidAmount,
        patientResponsibility:
          acc.patientResponsibility + item.patientResponsibility,
        providerAdjustment: acc.providerAdjustment + item.providerAdjustment,
        payerInitiated:
          acc.payerInitiated + (parseFloat(item.payerInitiated) || 0),
      }),
      {
        billedAmount: 0,
        coveredAllowedAmount: 0,
        paidAmount: 0,
        patientResponsibility: 0,
        providerAdjustment: 0,
        payerInitiated: 0,
      }
    );
    return totals;
  };

  const totals = calculateTotals();

  const handleDownload = () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);
    setTimeout(() => {
      pdfRef.current?.generatePdf();
    }, 100);
  };

  const handlePrint = () => {
    if (!contentRef.current) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const printContent = contentRef.current.innerHTML;
    printWindow.document.write(`
            <!DOCTYPE html>
            <html>
                <head>
                    <title>${title} - ${eraEobData.patientName}</title>
                    <style>
                        body {
                            font-family: Arial, sans-serif;
                            margin: 20px;
                            color: #000;
                        }
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                        }
                        th, td {
                            border: 1px solid #ddd;
                            padding: 8px;
                            text-align: left;
                            font-size: 12px;
                        }
                        th {
                            background-color: #f2f2f2;
                            font-weight: bold;
                        }
                        .section {
                            margin-bottom: 20px;
                        }
                        .section-title {
                            font-weight: bold;
                            margin-bottom: 10px;
                            font-size: 14px;
                        }
                        @media print {
                            @page {
                                margin: 0.5in;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${printContent}
                </body>
            </html>
        `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handlePdfSave = (blob: Blob) => {
    const fileName = `EOB_Details_${eraEobData.patientName.replace(/\s+/g, "_")}_${eraEobData.checkEftNumber}.pdf`;
    downloadPdf(blob, fileName);
    setIsGeneratingPdf(false);
    if (isAutoDownload) {
      onAutoDownloadComplete?.();
    }
  };

  useEffect(() => {
    if (
      isAutoDownload &&
      !autoDownloadTriggeredRef.current &&
      !isLoading &&
      !isFetching &&
      eraEobData?.uuid
    ) {
      autoDownloadTriggeredRef.current = true;
      handleDownload();
    }
  }, [isAutoDownload, isLoading, isFetching, eraEobData?.uuid]);

  useEffect(() => {
    autoDownloadTriggeredRef.current = false;
  }, [eraUuid, isAutoDownload]);

  const headerActions = (
    <Flex gap={2}>
      <Flex
        gap={0.5}
        alignItems="center"
        sx={{
          cursor: isGeneratingPdf ? "not-allowed" : "pointer",
          color: "primary.main",
          opacity: isGeneratingPdf ? 0.6 : 1,
        }}
        onClick={handleDownload}
      >
        <DownloadIcon fontSize="small" />
        <Text fontWeight={600}>Export as PDF</Text>
      </Flex>
      <Flex
        gap={0.5}
        alignItems="center"
        sx={{ cursor: "pointer", color: "primary.main" }}
        onClick={handlePrint}
      >
        <PrintIcon fontSize="small" />
        <Text fontWeight={600}>Print</Text>
      </Flex>
    </Flex>
  );

  const renderContent = () => (
    <Box
      ref={contentRef}
      sx={{
        p: 1.5,
        border: "1px solid #e3e7eb",
        borderRadius: "6px",
        background: "#fff",
      }}
    >
      <Text color="text.secondary" mb={1}>
        Cheque/EFT Date : {eraEobData.checkEftDate} (Cheque/EFT # :
        {eraEobData.checkEftNumber})
      </Text>

      <Flex
        gap={4}
        mb={1.5}
        flexWrap="wrap"
        sx={{ borderBottom: "1px solid #e3e7eb", pb: 1 }}
      >
        <Text variant="h6" fontWeight={700}>
          {eraEobData.patientName}
        </Text>
        <Text variant="h6" fontWeight={700}>
          Encounter Date&nbsp;-&nbsp; {eraEobData.encounterDate}
        </Text>
        <Text variant="h6" fontWeight={700}>
          Invoice #&nbsp;&nbsp; {eraEobData.invoiceNumber}
        </Text>
        <Text variant="h6" fontWeight={700}>
          ERA ID&nbsp;-&nbsp; {(eraEobData as any).eraDocNumber}
        </Text>
      </Flex>

      <Flex justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
        <Box>
          <Text color="primary.main" fontWeight={700} mb={0.5}>
            Payer Details
          </Text>
          <Text>
            {eraEobData.payerName} ({eraEobData.payerId})
          </Text>
          <Text>{eraEobData.payerAddress}</Text>
          <Flex gap={1}>
            <Text>Contact Name</Text>
            <Text color="text.secondary">{eraEobData.payerContactName}</Text>
          </Flex>
          <Flex gap={1}>
            <Text>E-Mail</Text>
            <Text color="text.secondary">{eraEobData.payerEmail}</Text>
          </Flex>
        </Box>
        <Box minWidth="220px">
          <Text color="primary.main" fontWeight={700} mb={0.5}>
            Payee Details
          </Text>
          <Text>{eraEobData.payeeName}</Text>
          <Text>{eraEobData.payeeAddress}</Text>
          <Flex gap={1}>
            <Text>NPI :</Text>
            <Text color="text.secondary">{eraEobData.payeeNpi}</Text>
          </Flex>
        </Box>
      </Flex>

      <Flex gap={1} mb={2} flexWrap="wrap">
        {[
          {
            label: "Cheque/EFT Amount",
            value: formatCurrency(eraEobData.checkEftAmount),
          },
          { label: "ERA Generated Date", value: eraEobData.eraGeneratedDate },
          { label: "Cheque/EFT Date", value: eraEobData.checkEftDate },
          { label: "Payment Method", value: eraEobData.paymentMethod },
          { label: "Cheque/EFT #", value: eraEobData.checkEftNumber },
        ].map((item) => (
          <Paper
            key={item.label}
            variant="outlined"
            sx={{ p: 1.5, minWidth: "170px", flex: "1 1 170px" }}
          >
            <Text color="text.secondary" mb={0.5}>
              {item.label}
            </Text>
            <Text variant="h6" fontWeight={700}>
              {item.value}
            </Text>
          </Paper>
        ))}
      </Flex>

      <Paper variant="outlined" sx={{ mb: 1.5 }}>
        <Box sx={{ px: 1.5, py: 0.7, bgcolor: "#eef0f2" }}>
          <Text fontWeight={700}>{eraEobData.patientName}</Text>
        </Box>
        <Flex justifyContent="space-between" gap={2} sx={{ px: 1.5, py: 1 }}>
          <Box>
            <Flex gap={2} mb={0.6}>
              <Text fontWeight={600}>Patient Control #</Text>
              <Text color="text.secondary">
                {eraEobData.patientControlNumber}
              </Text>
            </Flex>
            <Flex gap={2} mb={0.6}>
              <Text fontWeight={600}>Member ID</Text>
              <Text color="text.secondary">{eraEobData.memberId}</Text>
            </Flex>
            <Flex gap={2} mb={0.6}>
              <Text fontWeight={600}>Status</Text>
              <Text color="text.secondary">{eraEobData.status}</Text>
            </Flex>
            <Flex gap={2}>
              <Text fontWeight={600}>Insured Name</Text>
              <Text color="text.secondary">{eraEobData.insuredName}</Text>
            </Flex>
          </Box>
          <Box minWidth="260px">
            <Flex justifyContent="space-between" mb={0.6}>
              <Text fontWeight={700}>Claim Charge</Text>
              <Text fontWeight={700} color="primary.main">
                {formatCurrency(eraEobData.claimCharge)}
              </Text>
            </Flex>
            <Flex justifyContent="space-between" mb={0.8}>
              <Text fontWeight={700}>Claim Payment</Text>
              <Text fontWeight={700} color="success.main">
                {formatCurrency(eraEobData.claimPayment)}
              </Text>
            </Flex>
            <Text fontWeight={600}>Payer Claim Control #</Text>
            <Text color="text.secondary">
              {eraEobData.payerClaimControlNumber}
            </Text>
          </Box>
        </Flex>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#eef0f2" }}>
                <TableCell>
                  <Text variant="body2" fontWeight={700}>
                    Date of Service
                  </Text>
                </TableCell>
                <TableCell>
                  <Text variant="body2" fontWeight={700}>
                    Procedure(Units) Modifiers
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Billed Amount
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Covered/Allowed Amount
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Paid Amount
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Patient Responsibility
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Provider Adjustment
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    Payer Initiated
                  </Text>
                </TableCell>
                <TableCell>
                  <Text variant="body2" fontWeight={700}>
                    Payer Processed Procedure
                  </Text>
                </TableCell>
                <TableCell>
                  <Text variant="body2" fontWeight={700}>
                    Remark
                  </Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eraEobData.lineItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Text variant="body2">{item.dateOfService}</Text>
                  </TableCell>
                  <TableCell>
                    <Text variant="body2">
                      {item.procedure} ({item.units}) Modifiers:{" "}
                      {item.modifiers}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2">
                      {formatCurrency(item.billedAmount)}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2">
                      {formatCurrency(item.coveredAllowedAmount)}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2" color="success.main">
                      {formatCurrency(item.paidAmount)}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2">
                      {formatCurrency(item.patientResponsibility)}
                      {item.patientResponsibilityCode && (
                        <Text
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          ({item.patientResponsibilityCode})
                        </Text>
                      )}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2">
                      {formatCurrency(item.providerAdjustment)}
                      {item.providerAdjustmentCode && (
                        <Text
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          ({item.providerAdjustmentCode})
                        </Text>
                      )}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="body2">{item.payerInitiated || "-"}</Text>
                  </TableCell>
                  <TableCell>
                    <Text variant="body2">{item.payerProcessedProcedure}</Text>
                  </TableCell>
                  <TableCell>
                    <Text variant="body2">{item.remark || "-"}</Text>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: "#f7f8f9" }}>
                <TableCell colSpan={2}>
                  <Text variant="body2" fontWeight={700}>
                    Total
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700} color="primary.main">
                    {formatCurrency(totals.billedAmount)}
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    {formatCurrency(totals.coveredAllowedAmount)}
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700} color="success.main">
                    {formatCurrency(totals.paidAmount)}
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    {formatCurrency(totals.patientResponsibility)}
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    {formatCurrency(totals.providerAdjustment)}
                  </Text>
                </TableCell>
                <TableCell align="right">
                  <Text variant="body2" fontWeight={700}>
                    {totals.payerInitiated.toFixed(2)}
                  </Text>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 1.5, py: 1.2, borderTop: "1px solid #e3e7eb" }}>
          <Flex gap={4} mb={0.8}>
            <Flex gap={1}>
              <Text fontWeight={700}>PR</Text>
              <Text fontWeight={700}>Patient Responsibility</Text>
            </Flex>
            <Flex gap={1}>
              <Text fontWeight={700}>CO</Text>
              <Text fontWeight={700}>Contractual Obligations</Text>
            </Flex>
          </Flex>
          <Flex gap={4}>
            <Text color="text.secondary">PR 1 deductible Amount</Text>
            <Text color="text.secondary">
              CO 45 has large exceeds fee schedule/maximum allowable or
              contracted/legislated fee arrangement.
            </Text>
          </Flex>
        </Box>
      </Paper>
    </Box>
  );

  return (
    <>
      <SideSheetDrawer
        w="90vw"
        open={disclosureHost.opened}
        onClose={disclosureHost.close}
        title={title}
        endIcon={headerActions}
      >
        {isLoading || isFetching ? (
          <Box p={3}>
            <Text>Loading ERA/EOB details...</Text>
          </Box>
        ) : (
          renderContent()
        )}
      </SideSheetDrawer>

      <PdfGenerator ref={pdfRef} onSave={handlePdfSave}>
        {renderContent()}
      </PdfGenerator>
    </>
  );
};

export default ViewEraEob;
